<!-- src/App.svelte -->
<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { api, isLocalApiBase } from './lib/api.ts';
  import { isCloudMode } from './lib/supabase.ts';
  import { connect, ws } from './lib/ws.svelte.ts';
  import { agentStore } from './lib/stores/agents.svelte.ts';
  import { pmStore, hydrateWorkspaces } from './lib/stores/pm.svelte.ts';
  import AgentDrawer from './lib/components/AgentDrawer.svelte';
  import CardPanel from './lib/components/CardPanel.svelte';
  import CommandPalette from './lib/components/CommandPalette.svelte';
  import MigrationWizard from './lib/components/migration/MigrationWizard.svelte';
  import { applyVitals, updateVitalsCss } from './lib/stores/vitals.svelte.ts';
  import SharedBoardView from './lib/components/SharedBoardView.svelte';
  import LazyTerminalPanel from './lib/components/LazyTerminalPanel.svelte';
  import FileExplorer from './lib/components/FileExplorer.svelte';
  import NavRail, { type NavSection } from './lib/components/NavRail.svelte';
  import NavSidebar from './lib/components/NavSidebar.svelte';
  import FilePreview from './lib/components/FilePreview.svelte';
  import GitPanel from './lib/components/GitPanel.svelte';
  import AppMainView from './lib/components/AppMainView.svelte';
  import SettingsSubapp from './lib/components/SettingsSubapp.svelte';
  import Toaster from './lib/components/Toaster.svelte';
  import AgentStatusBar from './lib/components/AgentStatusBar.svelte';
  import ShortcutModal from './lib/components/ShortcutModal.svelte';
  import TabBar from './lib/components/TabBar.svelte';
  import { tabStore, loadTabs, openTab, closeActiveTab, reopenLastClosed, type TabEntry } from './lib/stores/tabs.svelte.ts';
  import { pushRecentCard } from './lib/card-recents.ts';
  import { initAuth, authStore } from './lib/stores/auth.svelte.ts';
  import { subscribeWorkspace, unsubscribeWorkspace } from './lib/stores/cloud-sync.svelte.ts';
  import { acceptInvitation } from './lib/stores/members.svelte.ts';
  import LoginPage from './lib/components/LoginPage.svelte';

  type Tab = 'overview' | 'board' | 'table' | 'gallery' | 'canvas' | 'agents' | 'activity' | 'tasks' | 'goals' | 'milestones' | 'portfolio' | 'workload' | 'pulse' | 'rules' | 'personas' | 'chat' | 'brand' | 'drive' | 'studio' | 'dev';
  type SettingsCategory =
    | 'general' | 'appearance' | 'profile' | 'notifications'
    | 'integrations' | 'data' | 'shortcuts'
    | 'labels' | 'templates' | 'statuses'
    | 'agents'
    | 'workspace' | 'teams' | 'members' | 'api' | 'applications';
  type ContentNavSection = Exclude<NavSection, 'settings'>;
  let activeTab     = $state<Tab>('board');
  let theme         = $state<'dark' | 'light'>('dark');
  let density       = $state<'compact' | 'comfortable' | 'spacious'>('comfortable');

  // ── Primary navigation ────────────────────────────────────────────────
  // The icon rail on the far left picks a NavSection; the contextual
  // sidebar shows that section's children; the main view is the active
  // tab. Each tab belongs to exactly one section — tabToSection() is the
  // reverse map so sync works both ways (e.g. command palette jumping
  // straight to a tab also moves the sidebar section).
  let navSection = $state<NavSection>('tasks');
  let previousNavSectionBeforeSettings = $state<ContentNavSection>('tasks');
  let settingsInitialCategory = $state<SettingsCategory | null>(null);
  let sidebarCollapsed = $state(
    localStorage.getItem('agentboard.sidebarCollapsed') === 'true'
  );

  function tabToSection(t: Tab): NavSection {
    switch (t) {
      case 'overview': case 'board': case 'table': case 'gallery': case 'canvas':
      case 'goals': case 'milestones': case 'portfolio': case 'workload':
      case 'pulse': case 'tasks':
        return 'tasks';
      case 'agents': case 'activity': case 'rules': case 'personas':
        return 'agents';
      case 'chat':
        return 'chat';
      case 'studio': case 'brand':
        return 'studio';
      case 'drive':
        return 'drive';
      case 'dev':
        return 'dev';
    }
  }

  // When a tab changes (from the command palette, keyboard nav, or the
  // sidebar itself), make sure the rail's active section matches.
  $effect(() => {
    const expected = tabToSection(activeTab);
    if (navSection !== expected && navSection !== 'settings') {
      navSection = expected;
    }
  });

  // First tab for each section — used when the user clicks a section
  // icon that doesn't contain the current activeTab.
  function firstTabFor(section: NavSection): Tab | null {
    switch (section) {
      case 'dev':      return 'dev';
      case 'tasks':    return 'board';   // kanban is the default Tasks view
      case 'agents':   return 'agents';
      case 'studio':   return 'studio';
      case 'drive':    return 'drive';
      case 'chat':     return 'chat';
      case 'settings': return null;      // settings has no main view
    }
  }

  function handleSelectSection(section: NavSection) {
    navSection = section;
    // If the current tab doesn't belong to this section, jump to the
    // first tab that does. Settings is the exception — it stays on the
    // previous view so closing settings restores your place.
    if (section !== 'settings' && tabToSection(activeTab) !== section) {
      const t = firstTabFor(section);
      if (t) activeTab = t;
    }
  }

  function openSettings(category?: SettingsCategory) {
    if (navSection !== 'settings') {
      previousNavSectionBeforeSettings = navSection;
    }
    settingsInitialCategory = category ?? null;
    navSection = 'settings';
  }

  function closeSettings() {
    settingsInitialCategory = null;
    handleSelectSection(previousNavSectionBeforeSettings);
  }

  // Human-readable view name for the topbar
  const viewNames: Record<Tab, string> = {
    overview: 'Overview', board: 'Board', table: 'Table', gallery: 'Gallery', canvas: 'Canvas',
    agents: 'Agents', activity: 'Activity', tasks: 'My Tasks',
    goals: 'Goals', milestones: 'Milestones', portfolio: 'Portfolio', workload: 'Workload',
    pulse: 'Pulse', rules: 'Rules', personas: 'Personas',
    chat: 'Chat', brand: 'Brand Assets', drive: 'Google Drive', studio: 'Studio',
    dev: 'Development',
  };
  const currentViewName = $derived(viewNames[activeTab]);
  const currentProjectName = $derived(
    pmStore.projects.find(p => p.id === pmStore.activeProjectId)?.name ?? ''
  );

  // ── Terminal drawer (VS Code-style bottom panel) ─────────────────────
  let terminalOpen   = $state(false);
  let terminalHeight = $state(280); // px — user-resizable via top drag handle
  let terminalMounted = $state(false); // lazy-mount so we don't open a WS until asked

  // ── File explorer / git / preview pane (VS Code primary sidebar) ────
  let fileExplorerOpen = $state(false);
  let fileExplorerRoot = $state<string | null>(null);
  let fileExplorerMounted = $state(false); // lazy-mount so we don't fetch until asked
  let selectedFilePath = $state<string | null>(null);
  type SidePaneTab = 'files' | 'git' | 'preview';
  let sidePaneTab = $state<SidePaneTab>('files');

  const allCardCount = $derived(
    Object.values(pmStore.boardDetails)
      .flatMap(bd => bd.columns.flatMap(c => c.cards))
      .length
  );
  let drawerAgentId  = $state<string | null>(null);
  let openCardId     = $state<string | null>(null);
  let paletteOpen    = $state(false);
  let devFileToOpen  = $state<string | null>(null);
  let migrationOpen  = $state(false);
  let shortcutsOpen  = $state(false);
  let focusMode      = $state(false);
  let sharedToken    = $state<string | null>(null);
  let activeViewConfigId = $state<string | null>(null);

  const activeViewConfig = $derived(
    activeViewConfigId ? pmStore.viewConfigs.find(v => v.id === activeViewConfigId) ?? null : null
  );

  function selectViewConfig(id: string) {
    activeViewConfigId = id;
    // Keep activeTab on 'board' so topbar still shows a valid view name
  }

  // ── Round 19: history nav (#8) ─────────────────────────────
  let tabHistory    = $state<Tab[]>(['board']);
  let historyIdx    = $state(0);
  let historyLock   = false; // prevent push during back/forward

  function pushHistory(tab: Tab) {
    if (historyLock) return;
    // Trim forward history when navigating from a back state
    if (historyIdx < tabHistory.length - 1) {
      tabHistory = tabHistory.slice(0, historyIdx + 1);
    }
    if (tabHistory[tabHistory.length - 1] !== tab) {
      tabHistory = [...tabHistory, tab];
      historyIdx = tabHistory.length - 1;
    }
  }

  function historyBack() {
    if (historyIdx <= 0) return;
    historyIdx--;
    historyLock = true;
    activeTab = tabHistory[historyIdx];
    historyLock = false;
  }
  function historyForward() {
    if (historyIdx >= tabHistory.length - 1) return;
    historyIdx++;
    historyLock = true;
    activeTab = tabHistory[historyIdx];
    historyLock = false;
  }

  // Track every tab change
  $effect(() => { pushHistory(activeTab); });

  // ── Tab bar activation ─────────────────────────────────────────────
  // Called by TabBar when the user clicks/activates a tab.
  function handleTabActivate(tab: TabEntry) {
    if (tab.type === 'view') {
      activeTab = tab.key as Tab;
    } else {
      // Card tab — open the card panel.
      openCard(tab.key);
    }
  }

  function openCard(id: string) {
    openCardId = id;
    const card = Object.values(pmStore.boardDetails)
      .flatMap((board) => board.columns.flatMap((column) => column.cards))
      .find((item) => item.id === id);
    if (card) {
      pushRecentCard({
        id: card.id,
        title: card.title,
        human_id: card.human_id ?? null,
      });
    }
  }

  // Open a view tab whenever activeTab changes via sidebar / command palette.
  // Guard: track both wsId+tab so a workspace switch re-opens the active tab
  // in the new workspace's tab list, and so we never call openTab for the
  // exact same (workspace, tab) pair twice in a row (prevents effect loops).
  let _lastTabSynced: string | null = null;
  $effect(() => {
    const wsId = pmStore.activeWorkspaceId;
    const tab = activeTab;
    const key = wsId ? `${wsId}:${tab}` : null;
    if (wsId && tab && key !== _lastTabSynced) {
      _lastTabSynced = key;
      const label = viewNames[tab] ?? tab;
      openTab(wsId, 'view', tab, label);
    }
  });

  // Open a card tab whenever a card panel is opened.
  $effect(() => {
    if (pmStore.activeWorkspaceId && openCardId) {
      // Build a label from the card's human_id + title if we can find it.
      const card = Object.values(pmStore.boardDetails)
        .flatMap(bd => bd.columns.flatMap(c => c.cards))
        .find(c => c.id === openCardId);
      const label = card
        ? [(card.human_id ?? ''), card.title].filter(Boolean).join(' ')
        : openCardId.slice(0, 8);
      openTab(pmStore.activeWorkspaceId, 'card', openCardId, label);
    }
  });

  function handleGlobalKeydown(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      paletteOpen = !paletteOpen;
    }
    // Terminal drawer: Ctrl+` (VS Code convention). We use Ctrl on all
    // platforms because Cmd+` on macOS is window-cycle.
    if (e.ctrlKey && e.key === '`') {
      e.preventDefault();
      toggleTerminal();
    }
    // File explorer pane: Cmd/Ctrl+B (VS Code convention)
    if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
      e.preventDefault();
      toggleFileExplorer();
    }
    // Collapse the contextual nav sidebar: Cmd/Ctrl+\ (Linear convention)
    if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
      e.preventDefault();
      sidebarCollapsed = !sidebarCollapsed;
      localStorage.setItem('agentboard.sidebarCollapsed', String(sidebarCollapsed));
    }
    // Close active tab: Ctrl/Cmd+W
    if ((e.metaKey || e.ctrlKey) && e.key === 'w') {
      e.preventDefault();
      if (pmStore.activeWorkspaceId) {
        closeActiveTab(pmStore.activeWorkspaceId);
        const next = tabStore.tabs[tabStore.activeIdx];
        if (next) handleTabActivate(next);
      }
    }
    // Reopen last closed tab: Ctrl/Cmd+Shift+T
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'T') {
      e.preventDefault();
      if (pmStore.activeWorkspaceId) {
        reopenLastClosed(pmStore.activeWorkspaceId);
        const next = tabStore.tabs[tabStore.activeIdx];
        if (next) handleTabActivate(next);
      }
    }
    // Focus mode toggle
    const target = e.target as HTMLElement;
    const inInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
    // Shortcut cheatsheet: ? (Shift+/) when not in an input
    if (e.key === '?' && !inInput) {
      e.preventDefault();
      shortcutsOpen = !shortcutsOpen;
    }
    if (e.key === '/' && !inInput && (activeTab === 'board' || activeTab === 'canvas')) {
      e.preventDefault();
      document.getElementById('board-filter')?.focus();
    }
    if (e.key === 'f' && !inInput && openCardId) {
      e.preventDefault();
      focusMode = !focusMode;
      document.body.classList.toggle('focus-mode', focusMode);
    }
    // Escape exits focus mode
    if (e.key === 'Escape' && focusMode) {
      focusMode = false;
      document.body.classList.remove('focus-mode');
    }
  }

  function toggleTerminal() {
    terminalOpen = !terminalOpen;
    if (terminalOpen) terminalMounted = true;
  }

  function toggleFileExplorer() {
    fileExplorerOpen = !fileExplorerOpen;
    if (fileExplorerOpen) fileExplorerMounted = true;
  }

  // ── Terminal drawer resize (drag the top edge) ───────────────────────
  let dragStartY     = 0;
  let dragStartHeight = 0;
  let dragging        = $state(false);

  function onTerminalDragStart(e: PointerEvent) {
    dragging        = true;
    dragStartY      = e.clientY;
    dragStartHeight = terminalHeight;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onTerminalDragMove(e: PointerEvent) {
    if (!dragging) return;
    const dy = e.clientY - dragStartY;
    // Dragging up (negative dy) grows the panel; clamp between 120 and 80% of viewport
    const next = dragStartHeight - dy;
    const max = Math.floor(window.innerHeight * 0.8);
    terminalHeight = Math.max(120, Math.min(max, next));
  }
  function onTerminalDragEnd(e: PointerEvent) {
    if (!dragging) return;
    dragging = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  }

  function toggleFocusMode() {
    focusMode = !focusMode;
    document.body.classList.toggle('focus-mode', focusMode);
  }

  // Keep CSS vars in sync with agent count
  $effect(() => {
    void agentStore.running.length;
    updateVitalsCss();
  });

  function toggleTheme() {
    theme = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', theme === 'light' ? 'light' : '');
    localStorage.setItem('agentboard-theme', theme);
  }

  onMount(async () => {
    await initAuth();

    // Check if we're viewing a shared board
    const path = window.location.pathname;
    const shareMatch = path.match(/^\/share\/([a-zA-Z0-9_-]+)$/);
    if (shareMatch) {
      sharedToken = shareMatch[1];
      return; // skip normal init for shared view
    }

    // Check for workspace invitation acceptance
    const inviteMatch = path.match(/^\/invite\/([a-f0-9]+)$/);
    if (inviteMatch && isCloudMode) {
      const token = inviteMatch[1];
      if (!authStore.user) {
        // Store token in sessionStorage; handle after auth completes
        sessionStorage.setItem('pendingInviteToken', token);
      } else {
        const result = await acceptInvitation(token);
        if (result.workspaceId) {
          window.history.replaceState({}, '', '/');
          await hydrateWorkspaces();
        }
      }
    }

    applyVitals();
    const hasLocalAgentBackend = isLocalApiBase();

    if (authStore.isCloudMode && !authStore.user) {
      // Hosted mode should land on the auth screen quietly instead of
      // firing unauthenticated PM requests into the console first.
      return;
    }

    connect();
    if (hasLocalAgentBackend) {
      try {
        const [queue, running] = await Promise.all([
          api<typeof agentStore.queued>('/api/v1/tasks/queue'),
          api<typeof agentStore.running>('/api/v1/tasks/running'),
        ]);
        agentStore.queued = queue;
        agentStore.running = running;
      } catch (e) {
        console.warn('Failed to hydrate agent state:', e);
      }
    }
    await hydrateWorkspaces();

    // Cloud mode: subscribe to the initial workspace after hydration.
    if (isCloudMode && pmStore.activeWorkspaceId) {
      subscribeWorkspace(pmStore.activeWorkspaceId);
    }

    // Load persisted tab list for this session.
    if (pmStore.activeWorkspaceId) {
      await loadTabs(pmStore.activeWorkspaceId);
      // Sync activeTab to whatever the first loaded tab is, so the main
      // content area matches the tab bar on reload.
      const firstLoaded = tabStore.tabs[tabStore.activeIdx];
      if (firstLoaded?.type === 'view') {
        activeTab = firstLoaded.key as Tab;
      } else if (firstLoaded?.type === 'card') {
        openCardId = firstLoaded.key;
      }
    }

    // Theme persistence
    const savedTheme = localStorage.getItem('agentboard-theme') as 'dark' | 'light' | null;
    if (savedTheme) {
      theme = savedTheme;
      document.documentElement.setAttribute('data-theme', savedTheme === 'light' ? 'light' : '');
    }

    // Density persistence
    const savedDensity = localStorage.getItem('agentboard-density') as typeof density | null;
    if (savedDensity) density = savedDensity;
    document.documentElement.setAttribute('data-density', density);
  });

  // ── Cloud mode: re-subscribe when the active workspace changes ──────
  $effect(() => {
    if (isCloudMode && pmStore.activeWorkspaceId) {
      subscribeWorkspace(pmStore.activeWorkspaceId);
    }
  });

  onDestroy(() => {
    if (isCloudMode) unsubscribeWorkspace();
  });

  // ── BroadcastChannel — workspace awareness across windows ───────────
  // Synchronous onMount so it can return a cleanup function.
  // Other windows listen for 'workspace-opened' to update their switcher
  // or show unread badges. v1: we just establish the channel + post;
  // listeners can be added in a follow-up without touching this setup.
  onMount(() => {
    const bc = new BroadcastChannel('agentboard');
    // Post immediately if a workspace is already active.
    if (pmStore.activeWorkspaceId) {
      bc.postMessage({ type: 'workspace-opened', workspaceId: pmStore.activeWorkspaceId });
    }
    // Re-post whenever the active workspace changes (reactive via $effect.root
    // so it tracks pmStore reactively without being inside the async onMount).
    const stop = $effect.root(() => {
      $effect(() => {
        if (pmStore.activeWorkspaceId) {
          bc.postMessage({ type: 'workspace-opened', workspaceId: pmStore.activeWorkspaceId });
        }
      });
      return () => bc.close();
    });
    return stop;
  });

  // ── Main content scroll persistence ─────────────────────────────────
  // Save the main scroll position to sessionStorage (per-window) with a
  // 200 ms debounce so rapid scrolling doesn't thrash storage.
  let mainEl = $state<HTMLElement | null>(null);
  let _scrollTimer: ReturnType<typeof setTimeout> | null = null;

  function onMainScroll() {
    if (_scrollTimer !== null) clearTimeout(_scrollTimer);
    _scrollTimer = setTimeout(() => {
      _scrollTimer = null;
      if (mainEl) {
        try {
          sessionStorage.setItem('agentboard.mainScrollTop', String(mainEl.scrollTop));
        } catch { /* ignore */ }
      }
    }, 200);
  }

  // Restore scroll after mount (best-effort — element may not be scrollable yet).
  $effect(() => {
    if (mainEl) {
      try {
        const saved = sessionStorage.getItem('agentboard.mainScrollTop');
        if (saved) mainEl.scrollTop = Number(saved);
      } catch { /* ignore */ }
    }
  });
</script>

<svelte:window onkeydown={handleGlobalKeydown} />

{#if authStore.isCloudMode && authStore.loading}
  <div class="auth-loading">
    <div class="auth-spinner"></div>
  </div>
{:else if authStore.isCloudMode && !authStore.user}
  <LoginPage />
{:else}

{#if sharedToken}
  <SharedBoardView token={sharedToken} />
{:else}
<div class="root">
  <div class="body">
    <NavRail
      activeSection={navSection}
      onSelectSection={(section) => {
        if (section === 'settings') {
          openSettings();
          return;
        }
        handleSelectSection(section);
      }}
      onBrandClick={() => { handleSelectSection('tasks'); }}
    />

    {#if navSection === 'settings'}
      <!-- Settings is a full-page subapp — replaces sidebar + main. -->
      <SettingsSubapp
        onClose={closeSettings}
        initialCategory={settingsInitialCategory ?? undefined}
        {theme}
        onToggleTheme={toggleTheme}
        {density}
        onSetDensity={(d) => {
          density = d;
          document.documentElement.setAttribute('data-density', d);
          localStorage.setItem('agentboard-density', d);
        }}
        onOpenImport={() => { migrationOpen = true; }}
      />
    {:else}
    {#if !sidebarCollapsed && navSection !== 'dev'}
      <NavSidebar
        section={navSection}
        {activeTab}
        onSelectTab={(t) => { activeTab = t; activeViewConfigId = null; }}
        {activeViewConfigId}
        onSelectViewConfig={selectViewConfig}
        onOpenSettings={(category) => openSettings(category as SettingsCategory | undefined)}
      />
    {/if}

    {#if fileExplorerMounted}
      <aside class="file-explorer-pane" class:open={fileExplorerOpen} aria-hidden={!fileExplorerOpen}>
        <nav class="side-pane-tabs">
          <button
            class="side-pane-tab"
            class:active={sidePaneTab === 'files'}
            onclick={() => { sidePaneTab = 'files'; }}
          >Files</button>
          <button
            class="side-pane-tab"
            class:active={sidePaneTab === 'git'}
            onclick={() => { sidePaneTab = 'git'; }}
          >Git</button>
          <button
            class="side-pane-tab"
            class:active={sidePaneTab === 'preview'}
            onclick={() => { sidePaneTab = 'preview'; }}
          >Preview</button>
        </nav>

        <div class="side-pane-body">
          {#if sidePaneTab === 'files'}
            <FileExplorer
              rootPath={fileExplorerRoot}
              onRootChange={(path) => { fileExplorerRoot = path; }}
              onSelectFile={(path) => {
                // Hoist the selected file path up and flip to the Preview tab.
                // The FilePreview component's $effect picks up the new path.
                selectedFilePath = path;
                sidePaneTab = 'preview';
              }}
            />
          {:else if sidePaneTab === 'git'}
            <GitPanel rootPath={fileExplorerRoot} />
          {:else if sidePaneTab === 'preview'}
            <FilePreview path={selectedFilePath} />
          {/if}
        </div>
      </aside>
    {/if}

    <main class="main">
      <div class="view-topbar">
        <div class="view-topbar-left">
          <button class="history-btn" onclick={historyBack} disabled={historyIdx <= 0} title="Back" aria-label="Back">←</button>
          <button class="history-btn" onclick={historyForward} disabled={historyIdx >= tabHistory.length - 1} title="Forward" aria-label="Forward">→</button>
          <span class="view-name">{currentViewName}</span>
          {#if currentProjectName && tabToSection(activeTab) === 'tasks'}
            <span class="view-sub">· {currentProjectName}</span>
          {/if}
        </div>
        <div class="view-topbar-right">
          <button class="vt-cmdk" onclick={() => { paletteOpen = true; }}>
            <span>Search & actions</span>
            <kbd>⌘K</kbd>
          </button>
          <button
            class="vt-btn"
            class:active={fileExplorerOpen}
            onclick={toggleFileExplorer}
            title="Toggle file explorer (⌘B)"
          >
            <span class="vt-btn-icon" aria-hidden="true">▸</span>
            <span>Files</span>
          </button>
          <button
            class="vt-btn"
            class:active={terminalOpen}
            onclick={toggleTerminal}
            title="Toggle terminal (Ctrl+`)"
          >
            <span class="vt-btn-icon" aria-hidden="true">&gt;_</span>
            <span>Terminal</span>
          </button>
          <div class="vt-live" class:connected={ws.connected} title={ws.connected ? 'Connected' : 'Offline'}>
            <span class="vt-live-dot"></span>
          </div>
        </div>
      </div>

      {#if pmStore.activeWorkspaceId}
        <TabBar
          workspaceId={pmStore.activeWorkspaceId}
          onActivate={handleTabActivate}
        />
      {/if}

      <AppMainView
        {activeTab}
        {activeViewConfig}
        devFileToOpen={devFileToOpen}
        onFileOpened={() => { devFileToOpen = null; }}
        onOpenCard={openCard}
        onOpenProjectBoard={(projectId) => {
          pmStore.activeProjectId = projectId;
          activeTab = 'board';
        }}
        onSelectAgent={(id) => { drawerAgentId = id; }}
      />
    </main>
    {/if}
  </div>

  {#if terminalMounted}
    <section
      class="terminal-drawer"
      class:open={terminalOpen}
      style="height: {terminalOpen ? terminalHeight : 0}px;"
      aria-hidden={!terminalOpen}
    >
      <div
        class="terminal-drag-handle"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize terminal panel"
        onpointerdown={onTerminalDragStart}
        onpointermove={onTerminalDragMove}
        onpointerup={onTerminalDragEnd}
        onpointercancel={onTerminalDragEnd}
      ></div>
      <LazyTerminalPanel />
    </section>
  {/if}
</div>
{/if}

<CardPanel
  cardId={openCardId}
  onClose={() => { openCardId = null; focusMode = false; document.body.classList.remove('focus-mode'); }}
  onOpenAgent={(agentId) => { openCardId = null; drawerAgentId = agentId; focusMode = false; document.body.classList.remove('focus-mode'); }}
  onOpenWorktree={(path) => {
    // User clicked "Open in explorer" on a card's linked worktree.
    // Set the shared root path and open the side pane (mount if needed).
    fileExplorerRoot    = path;
    fileExplorerMounted = true;
    fileExplorerOpen    = true;
    sidePaneTab         = 'files';
  }}
  {focusMode}
  onToggleFocus={toggleFocusMode}
/>

<AgentDrawer agentId={drawerAgentId} onClose={() => { drawerAgentId = null; }} />

<Toaster />

<AgentStatusBar onViewAll={() => { activeTab = 'agents'; navSection = 'agents'; }} />

{#if paletteOpen}
  <CommandPalette
    onClose={() => { paletteOpen = false; }}
    onOpenCard={openCard}
    onNavigate={(tab) => { activeTab = tab; }}
    navSection={navSection}
    onFileOpen={(path) => {
      devFileToOpen = path;
      if (navSection !== 'dev') handleSelectSection('dev');
      paletteOpen = false;
    }}
  />
{/if}

{#if shortcutsOpen}
  <ShortcutModal onClose={() => { shortcutsOpen = false; }} />
{/if}

{#if migrationOpen}
  <MigrationWizard
    workspaceId={Number(pmStore.activeWorkspaceId ?? 0)}
    projectId={Number(pmStore.activeProjectId ?? 0)}
    onClose={() => { migrationOpen = false; }}
  />
{/if}

{/if}

<style>
  .auth-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    background: oklch(0.07 0.015 275);
  }

  .auth-spinner {
    width: 28px;
    height: 28px;
    border: 2px solid oklch(0.30 0.05 275 / 0.30);
    border-top-color: oklch(0.72 0.28 310);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  :global(:root) {
    --pulse-rate: 10s;
    --pulse-intensity: 0.3;
    --active-agents: 0;

    /* ── Background depth (cool indigo-tinted darks, not neutral black) ──
       Each layer steps up the lightness slightly so a user can tell the
       nav rail, content sidebar, topbar, main view, and elevated surfaces
       apart without reading labels. Previously all four shell zones were
       within 0.015 of each other — visually identical. */
    --bg-base:        oklch(0.065 0.015 275);  /* shell void (rarely visible) */
    --bg-surface:     oklch(0.095 0.018 275);  /* main view + rail */
    --bg-elevated:    oklch(0.13  0.020 275);  /* content sidebar + panel headers */
    --bg-overlay:     oklch(0.17  0.022 275);  /* modal dialogs, floating popovers */
    --bg-contrast:    oklch(0.195 0.025 275);  /* strongest elevation — hover, active row */

    --text-primary:   oklch(0.93 0.015 310);
    --text-secondary: oklch(0.74 0.020 310);
    --text-muted:     oklch(0.48 0.015 310);
    --text-subtle:    oklch(0.32 0.012 310);

    /* ──────────────────────────────────────────────────────────────────
       AgentBoard organ palette — purple primary, gold warm, dark blue
       ambient depth, red alert. Four colors pick up the bio-technical
       vibe (amethyst + bioluminescent bacteria + deep ocean + arterial).
       Expose every swatch in 4 variants: deep / core / bright / halo.
       ────────────────────────────────────────────────────────────────── */

    /* Purple — primary brand, breathes through every active element */
    --ab-purple-deep:   oklch(0.42 0.22 310);
    --ab-purple-core:   oklch(0.62 0.28 310);
    --ab-purple-bright: oklch(0.80 0.26 310);
    --ab-purple-halo:   oklch(0.90 0.20 310);

    /* Gold — heat, recency, warmth. Replaces amber for card heat trails */
    --ab-gold-deep:     oklch(0.58 0.14 85);
    --ab-gold-core:     oklch(0.78 0.16 85);
    --ab-gold-bright:   oklch(0.88 0.17 85);
    --ab-gold-halo:     oklch(0.94 0.12 85);

    /* Dark blue — ambient depth, resting/idle state, background washes */
    --ab-blue-deep:     oklch(0.28 0.16 265);
    --ab-blue-core:     oklch(0.42 0.20 265);
    --ab-blue-bright:   oklch(0.58 0.22 265);
    --ab-blue-halo:     oklch(0.72 0.18 265);

    /* Red — urgent, overdue, alert, agent-failed states */
    --ab-red-deep:      oklch(0.45 0.22 25);
    --ab-red-core:      oklch(0.62 0.26 25);
    --ab-red-bright:    oklch(0.75 0.24 25);
    --ab-red-halo:      oklch(0.85 0.20 25);

    /* Legacy shortcuts — preserved so existing references don't break,
       but retuned to the new palette. Treat the unprefixed names as
       semantic tokens rather than colors. */
    --accent:         var(--ab-purple-core);
    --accent-green:   var(--ab-purple-core);
    --accent-blue:    var(--ab-blue-bright);
    --accent-amber:   var(--ab-gold-core);
    --accent-red:     var(--ab-red-core);
    --accent-orange:  var(--ab-gold-deep);

    --border-subtle:  oklch(0.30 0.03 275 / 0.35);
    --border-faint:   oklch(0.30 0.03 275 / 0.12);

    --radius-sm: 4px;
    --radius-md: 6px;
    --radius-lg: 10px;
    --radius-xl: 12px;

    --transition-fast: 100ms ease;
    --transition-med:  150ms ease;
    --transition-slow: 250ms ease;

    --font-mono: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace;
    --font-sans: system-ui, -apple-system, sans-serif;
  }

  /* Light theme override */
  :global([data-theme="light"]) {
    --bg-base:        oklch(0.98 0.002 240);
    --bg-surface:     oklch(0.96 0.003 240);
    --bg-elevated:    oklch(0.94 0.004 240);
    --bg-overlay:     oklch(0.92 0.005 240);

    --text-primary:   oklch(0.18 0.012 240);
    --text-secondary: oklch(0.35 0.010 240);
    --text-muted:     oklch(0.55 0.008 240);
    --text-subtle:    oklch(0.70 0.005 240);

    --border-subtle:  oklch(0.75 0.005 240 / 0.4);
    --border-faint:   oklch(0.75 0.005 240 / 0.15);
  }

  /* App-shell containment: html, body, and #app all lock to viewport
     height with overflow: hidden so the app never scrolls as a unit —
     only scroll containers inside the shell (message lists, file trees,
     board columns, etc.) scroll. This is the #1 thing that separates
     "native-feeling UI" from "web page pretending to be a UI".

     Also: root font-size bumped from browser default (usually 16px) to
     15px. Most of the UI uses rem units so this is the one knob that
     scales the whole app. 15px is the sweet spot for dense UI on a
     retina display — 16px felt bloated, 14px felt like a web page. */
  :global(html) {
    font-size: 15px;
  }
  :global(html),
  :global(body) {
    height: 100vh;
    height: 100dvh;  /* dynamic viewport height for mobile URL bar edge case */
    margin: 0;
    padding: 0;
    overflow: hidden;
    overscroll-behavior: none; /* kill pull-to-refresh + rubber-banding */
  }
  :global(body) {
    background: var(--bg-base);
    color: var(--text-primary);
    font-family: var(--font-sans);
    -webkit-font-smoothing: antialiased;
    transition: background-color 250ms ease, color 250ms ease;
  }
  :global(#app) {
    height: 100%;
    overflow: hidden;
  }

  /* Global <select> override — macOS native selects ignore CSS background
     unless you disable -webkit-appearance first. Without this rule, every
     dropdown in the app renders as a pure-white Aqua widget that clashes
     with the bio-technical theme. Custom chevron drawn via SVG data URI. */
  :global(select) {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-color: oklch(0.14 0.02 275 / 0.70);
    color: oklch(0.92 0.03 310);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    border-radius: 5px;
    padding: 0.42rem 1.75rem 0.42rem 0.7rem;
    font-size: 0.82rem;
    font-family: inherit;
    cursor: pointer;
    outline: none;
    /* Custom dropdown chevron — purple, so it matches the accent */
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'><path d='M2 4.5L6 8.5L10 4.5' stroke='%23c299ff' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>");
    background-repeat: no-repeat;
    background-position: right 0.55rem center;
    background-size: 12px 12px;
    /* Tell the browser to use a dark popup for the options list */
    color-scheme: dark;
    transition: border-color 100ms ease, background-color 100ms ease;
  }
  :global(select:hover) {
    border-color: oklch(0.72 0.28 310 / 0.35);
    background-color: oklch(0.16 0.03 275 / 0.80);
  }
  :global(select:focus) {
    border-color: oklch(0.72 0.28 310 / 0.50);
  }
  :global(select:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }
  /* The <option> popup is browser-chrome, very limited CSS support, but
     color-scheme: dark above plus these two rules keep it consistent
     where the browser does honor them (Chrome, Edge). */
  :global(select option) {
    background-color: oklch(0.11 0.02 275);
    color: oklch(0.92 0.03 310);
  }

  /* ── Global button utility classes (refinement #49) ──────────────
     Three tiers: .btn.primary, .btn.secondary, .btn.ghost. Same
     height, same padding, same transition everywhere. Components
     that already define their own `.btn` (SettingsSubapp, McpRegister)
     scope-override these via Svelte scoped CSS, so no conflict. */
  :global(.btn) {
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.42rem 0.82rem;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 100ms ease, border-color 100ms ease, color 100ms ease;
    font-family: inherit;
    line-height: 1.3;
  }
  :global(.btn:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }
  :global(.btn.primary) {
    background: oklch(0.30 0.18 310 / 0.45);
    color: oklch(0.96 0.06 310);
    border: 1px solid oklch(0.72 0.26 310 / 0.50);
  }
  :global(.btn.primary:hover:not(:disabled)) {
    background: oklch(0.38 0.22 310 / 0.58);
  }
  :global(.btn.secondary) {
    background: oklch(0.17 0.025 275);
    color: oklch(0.82 0.06 310 / 0.85);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
  }
  :global(.btn.secondary:hover:not(:disabled)) {
    background: oklch(0.22 0.03 275);
    border-color: oklch(0.40 0.08 310 / 0.50);
  }
  :global(.btn.ghost) {
    background: transparent;
    color: oklch(0.72 0.06 310 / 0.75);
    border: 1px solid transparent;
  }
  :global(.btn.ghost:hover:not(:disabled)) {
    background: oklch(0.17 0.025 275);
    color: oklch(0.94 0.08 310);
  }
  :global(.btn.danger) {
    background: oklch(0.30 0.18 25 / 0.30);
    color: oklch(0.92 0.14 25);
    border: 1px solid oklch(0.62 0.26 25 / 0.50);
  }
  :global(.btn.danger:hover:not(:disabled)) {
    background: oklch(0.40 0.22 25 / 0.45);
    border-color: oklch(0.68 0.28 25 / 0.70);
  }

  /* Focus mode — dims the body (nav rail, sidebar, main view). The
     CardPanel overlay is position:fixed so it stays visible on top. */
  :global(body.focus-mode) .root .body {
    opacity: 0;
    pointer-events: none;
    transition: opacity 200ms ease;
  }
  :global(body:not(.focus-mode)) .root .body {
    opacity: 1;
    pointer-events: auto;
    transition: opacity 200ms ease;
  }

  .root {
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    isolation: isolate;
  }

  /* ── Living membrane: soft radial gradient mesh ──────────────────────
     Previously used filter: blur(16px) — dropped entirely because blur
     filters create a stacking context that forces software compositing
     for every layer above them (which meant every card hover triggered
     a full recomposite pass). The gradient radii are wide enough that
     the mesh still looks soft without the blur. Animation is pure
     transform + opacity → GPU-composited, zero paint. */
  .root::before {
    content: '';
    position: absolute;
    inset: -10%;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(ellipse 72% 56% at 18% 22%, oklch(0.30 0.16 310 / 0.32), transparent 65%),
      radial-gradient(ellipse 62% 46% at 82% 18%, oklch(0.26 0.14 265 / 0.26), transparent 60%),
      radial-gradient(ellipse 78% 62% at 65% 88%, oklch(0.28 0.12 85  / 0.13), transparent 64%),
      radial-gradient(ellipse 52% 42% at 12% 85%, oklch(0.28 0.16 25  / 0.10), transparent 60%);
    will-change: transform, opacity;
    transform: translateZ(0);
    animation: membrane-drift calc(var(--pulse-rate, 10s) * 2) ease-in-out infinite alternate;
  }

  @keyframes membrane-drift {
    0%   { transform: translate3d(0,       0,    0) scale(1.00); opacity: 0.75; }
    50%  { transform: translate3d(1.5%, -1%,  0) scale(1.03); opacity: 1.00; }
    100% { transform: translate3d(-1%,   1.5%, 0) scale(1.02); opacity: 0.85; }
  }

  /* Film grain removed. Its mix-blend-mode: overlay forced the whole
     viewport into a blend compositing pass on every hover — single
     biggest remaining perf killer once the transitions were fixed. */

  /* Everything inside the root sits above the membrane */
  .root > * { position: relative; z-index: 2; }

  @media (prefers-reduced-motion: reduce) {
    .root::before { animation: none; }
  }

  /* ── View topbar ──────────────────────────────────────────────────
     Slim chrome that lives INSIDE the main view (not above the body).
     Holds the current view name on the left and utility controls on
     the right: ⌘K trigger, files/terminal toggles, live indicator. */
  .view-topbar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.6rem 1rem;
    height: 44px;
    background: oklch(0.10 0.02 275 / 0.78);
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.15);
    position: relative;
  }
  .view-topbar::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 1px;
    background: linear-gradient(90deg,
      transparent 0%,
      var(--ab-blue-bright) 10%,
      var(--ab-purple-bright) 50%,
      var(--ab-gold-core) 90%,
      transparent 100%);
    opacity: 0.35;
    pointer-events: none;
  }

  .view-topbar-left {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-width: 0;
  }
  .history-btn {
    background: transparent;
    border: 1px solid transparent;
    color: oklch(0.60 0.05 310 / 0.60);
    font-size: 0.85rem;
    padding: 0.15rem 0.35rem;
    border-radius: 3px;
    cursor: pointer;
    transition: background 100ms ease, color 100ms ease;
    line-height: 1;
  }
  .history-btn:hover:not(:disabled) {
    background: oklch(0.17 0.025 275);
    color: oklch(0.94 0.08 310);
  }
  .history-btn:disabled {
    opacity: 0.25;
    cursor: default;
  }
  .view-name {
    font-size: 0.88rem;
    font-weight: 600;
    color: oklch(0.94 0.06 310);
    letter-spacing: 0.005em;
    white-space: nowrap;
  }
  .view-sub {
    font-size: 0.72rem;
    color: oklch(0.55 0.04 310 / 0.6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .view-topbar-right {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-shrink: 0;
  }

  /* Compact utility button used in the view topbar */
  .vt-btn {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    background: oklch(0.14 0.02 275 / 0.55);
    border: 1px solid oklch(0.30 0.05 275 / 0.22);
    color: oklch(0.72 0.04 310 / 0.80);
    padding: 0.28rem 0.65rem;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.74rem;
    font-weight: 500;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .vt-btn:hover {
    background: oklch(0.18 0.03 275);
    color: oklch(0.92 0.06 310);
    border-color: oklch(0.72 0.28 310 / 0.3);
  }
  .vt-btn.active {
    background: oklch(0.22 0.10 310 / 0.35);
    border-color: oklch(0.72 0.28 310 / 0.45);
    color: oklch(0.94 0.10 310);
  }
  .vt-btn-icon {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.72rem;
    color: oklch(0.72 0.28 310 / 0.75);
    letter-spacing: -0.05em;
  }
  .vt-btn.active .vt-btn-icon {
    color: oklch(0.86 0.25 310);
  }

  /* ⌘K trigger — the classic Linear-style "search & actions" affordance */
  .vt-cmdk {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: oklch(0.14 0.02 275 / 0.55);
    border: 1px solid oklch(0.30 0.05 275 / 0.22);
    color: oklch(0.55 0.04 310 / 0.55);
    padding: 0.28rem 0.7rem;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.74rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
    min-width: 220px;
    text-align: left;
  }
  .vt-cmdk:hover {
    background: oklch(0.18 0.03 275);
    color: oklch(0.85 0.06 310);
    border-color: oklch(0.72 0.28 310 / 0.3);
  }
  .vt-cmdk span { flex: 1; }
  .vt-cmdk kbd {
    font-size: 0.65rem;
    color: oklch(0.72 0.04 310 / 0.55);
    background: oklch(0.10 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 3px;
    padding: 0.08rem 0.35rem;
    font-family: system-ui, sans-serif;
  }

  /* Compact live indicator in the view topbar */
  .vt-live {
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .vt-live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: oklch(0.35 0.03 275);
    transition: background-color 200ms ease;
  }
  .vt-live.connected .vt-live-dot {
    background: var(--ab-purple-bright);
    box-shadow:
      0 0 3px  oklch(0.80 0.26 310 / 0.90),
      0 0 8px  oklch(0.72 0.28 310 / 0.55),
      0 0 18px oklch(0.62 0.28 310 / 0.28);
    will-change: transform, opacity;
    animation: vt-live-breathe var(--pulse-rate, 10s) ease-in-out infinite;
  }
  @keyframes vt-live-breathe {
    0%, 100% { transform: scale(1);    opacity: 1;    }
    50%       { transform: scale(0.72); opacity: 0.45; }
  }
  @media (prefers-reduced-motion: reduce) {
    .vt-live-dot { animation: none; }
  }

  /* File explorer / git pane: collapsible left-side panel.
     NOTE: We do NOT animate width — width animations force the browser to
     re-layout every sibling on every frame, which is the single worst thing
     you can animate in a Chromium-based renderer. The pane just pops open
     or closed instantly. If we want a slide later, use a fixed-position
     overlay with transform: translateX, not inline width. */
  .file-explorer-pane {
    flex-shrink: 0;
    width: 0;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg,
      oklch(0.10 0.02 275 / 0.80) 0%,
      oklch(0.08 0.02 275 / 0.60) 100%);
    border-right: 1px solid oklch(0.30 0.05 275 / 0.15);
    position: relative;
  }
  .file-explorer-pane.open {
    width: 280px;
  }

  /* Right-edge filament on the shared pane (replaces the one that was
     inside FileExplorer so it covers both tabs). */
  .file-explorer-pane::after {
    content: '';
    position: absolute;
    top: 0; bottom: 0; right: 0;
    width: 1px;
    background: linear-gradient(180deg,
      transparent 0%,
      oklch(0.72 0.28 310 / 0.25) 50%,
      transparent 100%);
    box-shadow: 0 0 8px oklch(0.72 0.28 310 / 0.20);
    opacity: 0.55;
    will-change: opacity;
    animation: side-pane-filament var(--pulse-rate, 10s) ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes side-pane-filament {
    0%, 100% { opacity: 0.50; }
    50%       { opacity: 0.90; }
  }

  .side-pane-tabs {
    flex-shrink: 0;
    display: flex;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.15);
    background: oklch(0.08 0.02 275 / 0.5);
  }
  .side-pane-tab {
    flex: 1;
    background: transparent;
    border: none;
    color: oklch(0.55 0.05 310 / 0.55);
    padding: 0.5rem 0.5rem;
    cursor: pointer;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    transition: color 120ms ease, background 120ms ease, border-color 120ms ease;
    border-bottom: 1px solid transparent;
  }
  .side-pane-tab:hover {
    color: oklch(0.85 0.10 310);
    background: oklch(0.72 0.28 310 / 0.06);
  }
  .side-pane-tab.active {
    color: oklch(0.94 0.12 310);
    border-bottom-color: oklch(0.78 0.26 310 / 0.70);
  }

  .side-pane-body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  @media (prefers-reduced-motion: reduce) {
    .file-explorer-pane::after { animation: none; }
  }

  /* ── Terminal drawer (VS Code-style bottom panel) ─────────────────
     Same rule as the file-explorer-pane: no height animation, because
     animating height thrashes layout of .body and every view inside it
     for 180ms. The drawer pops open/closed instantly. */
  .terminal-drawer {
    flex-shrink: 0;
    width: 100%;
    height: 0;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    /* Ensure the drawer stacks above the body and below any overlays */
    z-index: 8;
  }
  /* .terminal-drawer.open height is driven by inline style so the user
     can drag-resize it freely — no class-level rule needed. */

  .terminal-drag-handle {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    cursor: ns-resize;
    z-index: 2;
    background: transparent;
    transition: background 120ms ease;
  }
  .terminal-drag-handle:hover {
    background: oklch(0.72 0.28 310 / 0.22);
  }
  .terminal-drag-handle:active {
    background: oklch(0.78 0.26 310 / 0.40);
  }

  /* When the body is present alongside the drawer, make .body take the
     remaining space. .body already has flex: 1, so this is mostly a
     min-height safety: a very tall terminal can shrink the main area down
     to a readable minimum without collapsing it. */
  .body {
    min-height: 200px;
  }

  /* ── Body ────────────────────────────────────────── */
  .body {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  .main {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .main > * {
    animation: fade-enter 200ms ease both;
  }

  @keyframes fade-enter {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Density system ──────────────────────────────── */
  :global([data-density="compact"]) {
    --card-padding: 0.45rem 0.625rem;
    --card-gap: 0.375rem;
    --col-padding: 0.5rem;
    --col-gap: 0.625rem;
    --font-scale: 0.93;
  }

  :global([data-density="comfortable"]),
  :global(:not([data-density])) {
    --card-padding: 0.75rem 0.875rem;
    --card-gap: 0.5rem;
    --col-padding: 0.75rem;
    --col-gap: 1rem;
    --font-scale: 1;
  }

  :global([data-density="spacious"]) {
    --card-padding: 1rem 1.125rem;
    --card-gap: 0.75rem;
    --col-padding: 1rem;
    --col-gap: 1.25rem;
    --font-scale: 1.05;
  }

  /* Density picker CSS removed — moved into NavSidebar settings section */
</style>
